import { useRouter } from 'next/router';
import { useState, ChangeEvent } from 'react';

import { useShowcaseTracking } from '@vimeo/picox';
import { useStorageLimitLockedVideoPaywall } from '@vimeo/video-library';
import { useViewer } from '@vimeo/viewer';
import { LoadMoreClips } from '@vimeo/viewer-showcase-ui/components/LoadMoreClips';
import {
  ShowcasePlaylist,
  ShowcasePlaylistSkeleton,
  ShowcasePlaylistErrorView,
  ShowcasePlaylistEmptyView,
} from '@vimeo/viewer-showcase-ui/components/ShowcasePlaylist';

import { DownloadListModule } from './DownloadListModule';
import { useGetInfiniteVideos } from '../hooks/use-get-playlist-videos';
import { useGetShowcase } from '../hooks/use-get-showcase';
import { useSelectedId } from '../hooks/use-selected-id';
import { useAlbumStore } from '../state/albumStore';
import { bpSelectToggleAutoplay, bpShowcaseVideoClick } from '../utils/analytics';

interface GalleryPlaylistModuleProps {
  playerContainerRef: React.RefObject<HTMLDivElement | null>;
  bannerHeight?: string;
}

export const GalleryPlaylistModule = ({
  playerContainerRef,
  bannerHeight,
}: GalleryPlaylistModuleProps) => {
  const router = useRouter();
  const {
    clips,
    total,
    isLoading,
    error,
    mutate,
    loadMoreClips,
    canLoadMoreClips,
    isLoadingMoreClips,
  } = useGetInfiniteVideos();
  const {
    allowContinuousPlay,
    allowDownloads,
    gridSize,
    showGridProfileImage,
    showGridProfileName,
    showGridVideoTitle,
    showGridVideoCard,
    showVideoCount,
    user,
  } = useGetShowcase();
  const { selectedId, selectedClipData } = useSelectedId();
  const { setContinuousPlay, isShowcaseEmbedded } = useAlbumStore((state) => ({
    isShowcaseEmbedded: state.isShowcaseEmbedded,
    setContinuousPlay: state.setContinuousPlay,
  }));
  const [activeDownloadClipId, setActiveDownloadClipId] = useState<string | null>(null);
  const viewer = useViewer();
  const { trackShowcaseVideoClicked } = useShowcaseTracking();
  const albumId = useAlbumStore((state) => state.albumId);
  const openStorageLimitLockedVideoPaywall = useStorageLimitLockedVideoPaywall();

  const onAutoplayChange = (checked: boolean) => {
    setContinuousPlay(checked);
  };

  const onAutoplayClick = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setContinuousPlay(checked);
    bpSelectToggleAutoplay(
      selectedId ?? '',
      checked,
      viewer,
      selectedClipData?.privacy?.view || '',
    );
  };

  const onVideoCardClick = (
    link: string,
    clipId: string,
    videoPrivacy: string,
    videoOwnerUri: string,
  ) => {
    bpShowcaseVideoClick(user?.uri || '', clipId, videoPrivacy, videoOwnerUri, viewer);
    const position = clips ? clips.findIndex((c) => c.clipId === clipId) + 1 : 0;
    if (position > 0) {
      trackShowcaseVideoClicked({
        showcaseId: albumId.toString(),
        clipId,
        showcaseVideoPosition: position,
        showcasePageNumber: 1,
      });
    }
    router.push(link);
    if (playerContainerRef.current && isShowcaseEmbedded && bannerHeight) {
      playerContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const onDownloadModalClose = () => {
    setActiveDownloadClipId(null);
  };

  const onDownloadClick = (clipId: string) => {
    setActiveDownloadClipId(clipId);
  };

  if (isLoading || !clips) {
    return (
      <ShowcasePlaylistSkeleton gridSize={gridSize} showVideoPlayerCards={showGridVideoCard} />
    );
  }

  if (error) {
    return <ShowcasePlaylistErrorView onRetry={() => mutate()} />;
  }

  if (clips.length === 0) {
    return <ShowcasePlaylistEmptyView />;
  }

  return (
    <>
      <ShowcasePlaylist
        showVideoCount={showVideoCount}
        clips={clips}
        activeClipId={selectedId}
        gridSize={gridSize}
        total={total}
        isAutoplayDefaultChecked={allowContinuousPlay}
        onVideoCardClick={onVideoCardClick}
        onAutoplayChange={onAutoplayChange}
        onAutoplayClick={onAutoplayClick}
        onLockedVideoClick={() =>
          openStorageLimitLockedVideoPaywall({
            location: 'showcase_viewer_video_card',
            paywallTrigger: 'showcase_viewer_locked_video',
          })
        }
        allowDownloads={allowDownloads}
        onDownloadClick={onDownloadClick}
        showVideoPlayerCards={showGridVideoCard}
        showProfileImage={showGridProfileImage}
        showProfileName={showGridProfileName}
        showVideoTitle={showGridVideoTitle}
      />
      {canLoadMoreClips && (
        <LoadMoreClips isLoadingMoreClips={isLoadingMoreClips} loadMoreClips={loadMoreClips} />
      )}
      {allowDownloads ? (
        <DownloadListModule
          isDownloadModalOpen={!!activeDownloadClipId}
          onClose={onDownloadModalClose}
          clipId={activeDownloadClipId}
        />
      ) : null}
    </>
  );
};
